<script setup lang="ts">
import immersveLogoLight from '@/assets/icons/immersve-logo-light.svg';
// import immersveLogoDark from '@/assets/icons/immersve-logo-dark.svg';

import x_light from '@/assets/icons/x-light.svg';
// import x_dark from '@/assets/icons/x-dark.svg';

import { useRouter } from 'vue-router';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useKycSessionStore } from '@/stores/kycSession.store';
import { storeToRefs } from 'pinia';
import { config } from '@/config';
import Mixpanel from '@/integrations/Mixpanel';

const partnerLogoUrls: Record<string, string> = {
  'bitget wallet': 'https://static.immersve.com/brand-assets/bitget/bitget-wallet-240x68-light.png',
  trustwallet: 'https://static.immersve.com/brand-assets/trustwallet/TrustWalletLogoSmall.png',
};

const router = useRouter();

const { kycSession } = storeToRefs(useKycSessionStore());

const stepNames = ref<string[]>([]);
const currentStep = ref<string | undefined>();

const navBar = ref<HTMLElement | undefined>();
const dot = ref<(HTMLElement | undefined)[] | undefined>();

function updateSteps() {
  if (!kycSession.value) {
    return;
  }
  const steps = kycSession.value.steps;

  stepNames.value = steps.steps.map((step) => step.name);
  const stepForScreen = steps.getCurrentStepForScreen(router.currentRoute.value.name as string);
  if (!stepForScreen) {
    return;
  }
  currentStep.value = stepForScreen.name;
}

updateSteps();

function onExitClick() {
  if (kycSession.value?.redirectUrl) {
    window.location.href = kycSession.value.redirectUrl;
  }
  Mixpanel.instance.reset();
}

const progressLength = computed(() => {
  if (!currentStep.value || !navBar.value) {
    return '0%';
  }

  const stepIndex = stepNames.value.indexOf(currentStep.value ?? '');
  const totalSteps = stepNames.value.length;

  if (stepIndex === 0) {
    return '0%';
  }
  return `${Math.round(((stepIndex + 1) / totalSteps) * 100)}%`;
});

const componentKey = ref(0);

function forceRerender() {
  componentKey.value += 1;
}

onMounted(() => {
  window.addEventListener('resize', forceRerender);
});

onUnmounted(() => {
  window.removeEventListener('resize', forceRerender);
});

//dark mode tracking
const isDarkTheme = ref(
  window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
);

function updateTheme(e: MediaQueryListEvent) {
  isDarkTheme.value = e.matches;
}

if (window.matchMedia) {
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.addEventListener('change', updateTheme);
  onUnmounted(() => {
    mediaQuery.removeEventListener('change', updateTheme);
  });
}

const icons = computed(() => {
  // TODO: this is hardcoded to light mode, need to fix if we want to add darkmode
  return {
    logo: immersveLogoLight,
    x_close: x_light,
  };
  // return isDarkTheme.value
  //   ? {
  //       logo: immersveLogoDark,
  //       x_close: x_dark,
  //     }
  //   : {
  //       logo: immersveLogoLight,
  //       x_close: x_light,
  //     };
});
const partnerName = computed(() => {
  return kycSession.value?.partner?.name?.toLowerCase().trim();
});
const hasCustomLogo = computed(() => {
  return (
    partnerName.value &&
    Object.keys(partnerLogoUrls).some((allowedName) => allowedName === partnerName.value)
  );
});
const logo = computed(() => {
  if (config.logoOverrideUrl) {
    return config.logoOverrideUrl;
  }

  if (partnerName?.value && partnerLogoUrls[partnerName.value]) {
    return partnerLogoUrls[partnerName?.value];
  }
  return icons.value.logo;
});
const logoAlt = computed(() => {
  return `${partnerName.value} logo`;
});
</script>

<template>
  <div
    class="container mx-auto max-w-xl px-4 lg:container"
    v-if="currentStep"
    :key="componentKey"
    data-test="session-progress-header"
  >
    <header>
      <div class="mt-4 flex items-center justify-between sm:mt-0 sm:pt-7 lg:items-baseline">
        <!-- Logo container -->
        <div class="partner-logo w-[148px]" v-if="hasCustomLogo">
          <img class="h-[42px]" :src="logo" :alt="logoAlt" />
        </div>
        <div class="logo w-[140px]" v-else>
          <img class="h-[42px]" :src="logo" :alt="logoAlt" />
        </div>
        <!-- Status bar -->
        <div class="relative hidden w-[540px] flex-col justify-end lg:flex" ref="navBar">
          <div class="dark:bg-dark-brd-dark bg-light-border absolute top-0 h-0.5 w-full">
            <div
              class="bg-light-primary dark:bg-gradient-imsv h-0.5"
              :style="`width: ${progressLength}`"
            />
          </div>
          <ul class="z-auto flex w-full justify-between">
            <li
              v-for="step in stepNames"
              v-bind:key="step"
              class="mt-3 flex flex-col items-center text-center text-sm tracking-tight"
            >
              <!--Dot-->
              <div
                v-if="step === currentStep"
                class="bg-light-primary dark:bg-dark-brd-light absolute -top-1 h-2.5 w-2.5 rounded-full"
                :style="`left: ${progressLength}`"
              >
                <span ref="dot"></span>
              </div>
            </li>
          </ul>
        </div>
        <div class="flex w-[140px] justify-end">
          <button
            @click.stop="onExitClick"
            class="dark:border-dark-brd-dark border-light-border flex h-12 w-14 cursor-pointer flex-col items-center justify-center rounded-lg border"
            v-if="kycSession?.redirectUrl"
            data-test="exit"
          >
            <span class="flex items-center gap-2"
              ><img :src="icons.x_close" class="inline h-4 w-4"
            /></span>
          </button>
        </div>
      </div>
      <!-- Status bar for mobile -->
      <div
        class="dark:bg-dark-brd-dark bg-light-border mt-6 mb-3 flex h-0.5 w-full items-center lg:hidden"
      >
        <div
          class="bg-light-primary dark:bg-gradient-imsv relative h-0.5"
          :style="`width: ${progressLength}`"
        >
          <div
            class="bg-light-primary dark:bg-dark-brd-light absolute -top-1 right-0 h-2.5 w-2.5 rounded-full"
          >
            <span></span>
          </div>
        </div>
      </div>
    </header>
  </div>
</template>
